import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { tagKurz } from "../daten/betriebstag";
import type { Geraetestueck } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Das Gerät an einer Position — eine Marke, die am Ort aufklappt.

   Paket 9, Rückmeldung Luca 07.10.2026: Ein Klick auf „Gerät MB-26"
   sprang aus der Akte in die Planung. Wer nur wissen wollte, welcher
   Bagger das ist und wie lange er belegt ist, stand danach in einer
   anderen Maske und musste den Weg zurück suchen.

   Jetzt klappt die Marke **am Ort** ein kleines Blatt auf: Kennung,
   Name, belegt von–bis — und, wo es etwas zu wählen gibt, die Wahl
   (`inhalt`). Die Planung ist ein ausdrücklicher, leiser Verweis
   darunter („In der Planung zeigen"), kein Nebeneffekt des Klicks.

   **Das Blatt hängt im Dokument, nicht in der Zelle.** Die Positionsliste
   rollt seitlich (`.tab-fenster`), und ein Blatt in der Zelle würde am
   Rand der Tabelle abgeschnitten. Es steht darum fest über der Seite,
   unter der Marke, und rückt beim Rollen mit.
   ══════════════════════════════════════════════════════════════════ */

type Lage = { links: number; oben: number; ueber: boolean };

const ABSTAND = 6;
const RAND = 12;

export function Geraeteklappe({
  nr, g, rolleWort, name, gestrichelt = false, bis, inhalt, klasse = "",
}: {
  /** Der Vorgang — die Planung öffnet ihn markiert. */
  nr: string;
  g: Geraetestueck;
  /** „Gerät", „Anbau", „Zubehör" — vor der Kennung, leise. */
  rolleWort: string;
  /** Der Name hinter der Kennung — leer, wenn er nur die Bezeichnung der Zeile wiederholt. */
  name?: string | undefined;
  /** Anbau und Zubehör: gestrichelt, damit der Träger vorne steht. */
  gestrichelt?: boolean;
  /** Das Ende der Belegung — die Miete endet mit der Position. */
  bis?: string | null | undefined;
  /** Was im Blatt zu wählen ist — das Stück tauschen, Anbauteile. */
  inhalt?: ReactNode;
  klasse?: string;
}) {
  const t = useT();
  const id = useId();
  const gehZu = useNavigate();
  const [offen, setzeOffen] = useState(false);
  const [lage, setzeLage] = useState<Lage | null>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const blatt = useRef<HTMLDivElement>(null);

  /* Unter die Marke, im Fenster gehalten: Reicht der Platz darunter nicht,
     steht es darüber; rechts rückt es zurück, bis es ganz zu sehen ist. */
  const messen = () => {
    const a = knopf.current?.getBoundingClientRect();
    const b = blatt.current?.getBoundingClientRect();
    if (!a) return;
    const breite = b?.width ?? 320;
    const hoehe = b?.height ?? 0;
    const links = Math.max(RAND, Math.min(a.left, window.innerWidth - breite - RAND));
    const ueber = a.bottom + ABSTAND + hoehe > window.innerHeight - RAND && a.top - ABSTAND - hoehe > RAND;
    setzeLage({ links, oben: ueber ? a.top - ABSTAND - hoehe : a.bottom + ABSTAND, ueber });
  };

  useLayoutEffect(() => {
    if (!offen) { setzeLage(null); return; }
    messen();
    // Nach dem ersten Zeichnen kennt das Blatt seine Größe.
    const r = requestAnimationFrame(messen);
    return () => cancelAnimationFrame(r);
  }, [offen]);

  useEffect(() => {
    if (!offen) return undefined;
    /* Ein Klick daneben schließt; einer in der Marke schaltet selbst. */
    const daneben = (e: MouseEvent) => {
      const ziel = e.target as Node;
      if (blatt.current?.contains(ziel) || knopf.current?.contains(ziel)) return;
      setzeOffen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setzeOffen(false);
      knopf.current?.focus();
    };
    const mit = () => messen();
    document.addEventListener("mousedown", daneben);
    document.addEventListener("keydown", taste, true);
    window.addEventListener("scroll", mit, true);
    window.addEventListener("resize", mit);
    return () => {
      document.removeEventListener("mousedown", daneben);
      document.removeEventListener("keydown", taste, true);
      window.removeEventListener("scroll", mit, true);
      window.removeEventListener("resize", mit);
    };
  }, [offen]);

  const von = g.von ? tagKurz(g.von) : "";
  const ende = bis ? tagKurz(bis) : "";

  return (
    <>
      <button
        ref={knopf}
        type="button"
        className={`pos-geraet${gestrichelt ? " dran" : ""}${klasse ? ` ${klasse}` : ""}`}
        aria-expanded={offen}
        aria-controls={offen ? `${id}-blatt` : undefined}
        aria-haspopup="dialog"
        onClick={() => setzeOffen((o) => !o)}
      >
        <span className="leise">{rolleWort}</span>
        <span className="kennung">{g.kennung}</span>
        {name ? <span className="pos-geraet-name">{name}</span> : null}
        {/* „vorgemerkt" nur am Träger: Im Entwurf ist alles vorgemerkt, und
            dreimal dasselbe Wort in einer Zeile ist Lärm. Im Blatt steht es. */}
        {g.vorgemerkt && !gestrichelt ? <span className="leise">{t("positionen.geraet.vorgemerkt")}</span> : null}
      </button>
      {offen ? createPortal(
        <div
          ref={blatt}
          id={`${id}-blatt`}
          className="geraet-klappe"
          role="dialog"
          aria-label={t("positionen.geraet.klappe", { kennung: g.kennung })}
          style={lage
            ? { left: lage.links, top: lage.oben }
            // Vor dem Messen unsichtbar, damit es nicht in der Ecke aufblitzt.
            : { left: 0, top: 0, visibility: "hidden" }}
        >
          <div className="geraet-klappe-kopf">
            <span className="kennung">{g.kennung}</span>
            <b>{g.typ}</b>
          </div>
          <dl className="geraet-klappe-angaben">
            {von ? (
              <div>
                <dt>{t("positionen.geraet.belegt")}</dt>
                <dd>{ende ? t("positionen.karte.zeitraum", { von, bis: ende }) : von}</dd>
              </div>
            ) : null}
            {g.vorgemerkt || g.unterwegs ? (
              <div>
                <dt>{t("positionen.geraet.stand")}</dt>
                <dd>{t(g.unterwegs ? "positionen.geraet.unterwegs" : "positionen.geraet.vorgemerktSatz")}</dd>
              </div>
            ) : null}
          </dl>
          {inhalt ? <div className="geraet-klappe-inhalt">{inhalt}</div> : null}
          <div className="geraet-klappe-fuss">
            <button
              type="button"
              className="geraet-klappe-verweis"
              onClick={() => {
                setzeOffen(false);
                void gehZu({ to: "/kalender", search: { vorgang: nr, ...(g.von ? { von: g.von } : {}) } });
              }}
            >
              {t("positionen.geraet.imBrett")}
            </button>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
